/**
 * /relationships/:characterId/album — 回憶相簿.
 *
 * A scroll-feed of memorable moments for one (fan, character)
 * relationship. v1 pulls from the GET /api/fan/relationships/{id}
 * payload we already fetch on RelationshipDetail:
 *   - Milestones (level-crossings)
 *   - Level unlocks (character quotes + stickers per level)
 *   - First-person memory card
 * Future iters can fold in chat-history highlights + mini-app
 * completion records once those become discrete events.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getMyRelationship,
  type AffinityLevelName,
  type RelationshipDetail,
  type LevelUnlock,
  type AffinityMilestone,
} from '../api/fan-api';
import { useDocumentTitle } from '../lib/useDocumentTitle';

const LEVEL_TONE: Record<AffinityLevelName, { bg: string; text: string; border: string; gradient: string }> = {
  '陌生':       { bg: 'bg-slate-800',      text: 'text-slate-300',   border: 'border-slate-700',       gradient: 'from-slate-800 to-slate-900' },
  '點頭之交':   { bg: 'bg-sky-500/15',     text: 'text-sky-300',     border: 'border-sky-500/40',      gradient: 'from-sky-500/20 to-sky-500/5' },
  '朋友':       { bg: 'bg-blue-500/15', text: 'text-blue-300', border: 'border-blue-500/40',  gradient: 'from-blue-500/20 to-blue-500/5' },
  '親近':       { bg: 'bg-amber-500/15',   text: 'text-amber-300',   border: 'border-amber-500/40',    gradient: 'from-amber-500/20 to-amber-500/5' },
  '摯友':       { bg: 'bg-rose-500/15',    text: 'text-rose-300',    border: 'border-rose-500/40',     gradient: 'from-rose-500/20 to-rose-500/5' },
  '靈魂相依':   { bg: 'bg-violet-500/15',  text: 'text-violet-300',  border: 'border-violet-500/40',   gradient: 'from-violet-500/20 to-violet-500/5' },
};

/** Combines milestones with the corresponding unlock pack data for
 *  the same level, returning an enriched list sorted newest first. */
function buildEntries(detail: RelationshipDetail): Array<{
  key: string;
  level: number;
  levelName: AffinityLevelName;
  crossedAt: string;
  context?: string;
  stickerEmoji: string;
  stickerUrl: string | null;
  voiceUrl: string | null;
  characterQuote: string;
}> {
  const unlockByLevel = new Map<number, LevelUnlock>();
  for (const u of detail.unlocks) unlockByLevel.set(u.level, u);
  const entries = (detail.milestones as AffinityMilestone[]).map((m, i) => {
    const pack = unlockByLevel.get(m.level);
    return {
      key: `${m.crossedAt}-${i}`,
      level: m.level,
      levelName: m.levelName,
      crossedAt: m.crossedAt,
      context: m.context,
      stickerEmoji: pack?.stickerEmoji ?? '✨',
      stickerUrl: pack?.stickerUrl ?? null,
      voiceUrl: pack?.voiceUrl ?? null,
      characterQuote: pack?.characterQuote ?? '',
    };
  });
  entries.sort((a, b) => (a.crossedAt < b.crossedAt ? 1 : -1));
  return entries;
}

export function RelationshipAlbum() {
  const { characterId } = useParams<{ characterId: string }>();
  useDocumentTitle('回憶相簿');
  const [detail, setDetail] = useState<RelationshipDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!characterId) return;
    let alive = true;
    void getMyRelationship(characterId)
      .then((d) => { if (alive) setDetail(d); })
      .catch((e) => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, [characterId]);

  if (!characterId) return null;

  if (err) {
    return (
      <div className="max-w-md mx-auto px-3 sm:px-4 py-6">
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5 space-y-4 text-center">
          <div className="h-16 w-16 rounded-full bg-rose-500/15 border border-rose-500/40 flex items-center justify-center mx-auto text-rose-300 text-3xl">✕</div>
          <h1 className="text-xl font-semibold">無法載入</h1>
          <p className="text-slate-400 text-sm break-words">{err}</p>
          <Link to={`/relationships/${encodeURIComponent(characterId)}`} className="block w-full rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 py-3">回關係頁</Link>
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="max-w-md mx-auto px-3 sm:px-4 py-12 text-center">
        <div className="inline-block w-10 h-10 border-[3px] border-slate-700 border-t-indigo-400 rounded-full animate-spin" aria-label="載入中" />
      </div>
    );
  }

  const entries = buildEntries(detail);
  const currentTone = LEVEL_TONE[detail.levelName];

  return (
    <div className="max-w-md mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
      {/* Top header — back to detail + page title */}
      <header className="flex items-center justify-between gap-2">
        <Link
          to={`/relationships/${encodeURIComponent(detail.characterId)}`}
          className="text-slate-400 hover:text-slate-200 text-sm inline-flex items-center gap-1"
          aria-label="返回關係頁"
        >← 返回</Link>
        <h1 className="text-lg sm:text-xl font-semibold">回憶相簿</h1>
        <span className="w-12" aria-hidden="true" />
      </header>

      {/* Album cover — current relationship status. */}
      <section className={`rounded-2xl ${currentTone.border} border bg-gradient-to-br ${currentTone.gradient} p-5 text-center space-y-2`}>
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-300">{detail.characterName}</div>
        <div className={`text-4xl font-bold ${currentTone.text}`}>Lv {detail.level} · {detail.levelName}</div>
        <div className="text-slate-300 text-xs">好感度 {detail.value} / 100 · {entries.length} 個里程碑</div>
      </section>

      {/* Memory card excerpt */}
      {detail.memoryCard && (
        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4 sm:p-5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-rose-400 mb-2">角色寫給你的話</div>
          <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed border-l-2 border-rose-500/40 pl-3 italic">
            {detail.memoryCard}
          </p>
          {detail.memoryCardUpdatedAt && (
            <p className="text-[10px] text-slate-500 mt-2">{new Date(detail.memoryCardUpdatedAt).toLocaleString()} 更新</p>
          )}
        </section>
      )}

      {/* Milestone entries — vertical scroll feed, newest first */}
      {entries.length > 0 ? (
        <section className="space-y-3">
          <div className="text-[11px] uppercase tracking-[0.2em] text-amber-400 px-1">里程碑回憶</div>
          {entries.map((e) => {
            const tone = LEVEL_TONE[e.levelName];
            return (
              <article
                key={e.key}
                className={`rounded-2xl ${tone.border} border bg-gradient-to-br ${tone.gradient} p-5 space-y-3`}
              >
                <div className="flex items-center gap-3">
                  {e.stickerUrl ? (
                    <img
                      src={e.stickerUrl}
                      alt={`Lv ${e.level} 紀念貼圖`}
                      className="h-20 w-20 rounded-xl object-cover border border-slate-700 shrink-0"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-5xl shrink-0" aria-hidden="true">{e.stickerEmoji}</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs uppercase tracking-wider ${tone.text}`}>Lv {e.level} 達成</div>
                    <div className={`text-xl font-bold ${tone.text} mt-0.5`}>{e.levelName}</div>
                    <div className="text-[10px] text-slate-500 tabular-nums mt-0.5">
                      {new Date(e.crossedAt).toLocaleString()}
                    </div>
                  </div>
                </div>
                {e.voiceUrl && (
                  <audio controls src={e.voiceUrl} className="w-full h-9" preload="none" aria-label={`Lv ${e.level} 紀念語音`} />
                )}
                {e.characterQuote && (
                  <p className="text-sm text-slate-100 italic leading-relaxed">
                    「{e.characterQuote}」
                  </p>
                )}
                {e.context && (
                  <p className="text-xs text-slate-400 bg-slate-950/40 rounded-lg px-3 py-2 border border-slate-800">
                    當時的對話片段：<span className="text-slate-300">「{e.context}」</span>
                  </p>
                )}
              </article>
            );
          })}
        </section>
      ) : (
        <section className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/40 p-6 text-center text-slate-500 text-sm">
          還沒有任何里程碑。跟 {detail.characterName} 多聊一些，第一個回憶就會出現在這裡 ✨
        </section>
      )}

      <Link
        to={`/c/${encodeURIComponent(detail.characterId)}/chat`}
        className="block w-full rounded-2xl bg-gradient-to-r from-violet-500 to-violet-400 hover:from-violet-400 hover:to-violet-300 text-white font-medium py-4 text-center shadow-lg shadow-violet-500/20 active:scale-[0.99] transition"
      >
        <span className="text-2xl mr-2" aria-hidden="true">💬</span>
        繼續寫下新的回憶
      </Link>
    </div>
  );
}
